import { useNotification, Modal } from '@dispatch/web/ui';
import { Button, Chip } from '@mui/material';
import React from 'react';
import AddIcon from '@mui/icons-material/Add';
import { isEmpty, cloneDeep } from 'lodash';
import { UseFormSetValue, useForm } from 'react-hook-form';
import {
  CertificationAndLicense,
  CertsAndLicenseEdit,
  Resource,
  ResourceForm,
  ResultStatus,
  SelectedCertificationsAndLicenses,
  UpsertResource,
} from '@dispatch/shared/types';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { CertsAndLicenseModalList } from './CertsAndLicenseModalList';
import { CertsAndLicenseModalEdit } from './CertsAndLicenseModalEdit';
import { v4 as uuidv4 } from 'uuid';
import { updateCertsAndLicenses } from '@dispatch/shared';

const schema = yup
  .object({
    licenseNumber: yup.string().required('License Number is required'),
    issueDate: yup.string().required('Issue Date is required').nullable(),
    requireExpirationDate: yup.boolean(),
    expirationDate: yup
      .string()
      .nullable()
      .when('requireExpirationDate', {
        is: true,
        then: yup.string().required('Expiration Date is Required').nullable(),
      }),
  })
  .required();

export interface AssignCertsOrLicenseProps {
  resource: Resource;
  certsAndLicense: CertificationAndLicense[];
  upsertResource: UpsertResource;
  upsertResourceStatus?: ResultStatus;
  editState: {
    editData: CertsAndLicenseEdit | null;
    setEditData: React.Dispatch<CertsAndLicenseEdit | null>;
  };
  setValue: UseFormSetValue<ResourceForm>;
  isReadOnly?: boolean;
}

export const AssignCertOrLicense = ({
  resource,
  certsAndLicense,
  upsertResource,
  upsertResourceStatus,
  editState,
  setValue,
  isReadOnly,
}: AssignCertsOrLicenseProps) => {
  const { editData, setEditData } = editState;
  const { notifyError } = useNotification();
  const [open, setOpen] = React.useState(false);
  const [tableData, setTableData] = React.useState<CertificationAndLicense[]>(
    []
  );
  const defaultValues: CertsAndLicenseEdit = {
    licenseNumber: '',
    issueDate: new Date().toISOString(),
    expirationDate: '',
    verificationType: '',
    name: '',
    organization: '',
    description: '',
    stateProvince: [],
    mode: 'assign',
    isActive: false,
    requireExpirationDate: false,
    resourceCertId: '',
    integrationGUID: '',
  };

  const {
    control: formControl,
    formState: { errors: formErrors },
    handleSubmit,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  React.useEffect(() => {
    if (certsAndLicense?.length > 0) {
      setTableData(certsAndLicense);
    }
  }, [certsAndLicense]);

  React.useEffect(() => {
    if (!isEmpty(editData)) {
      reset({ ...editData });
      setOpen(true);
    } else if (!open) {
      reset(defaultValues);
    }
  }, [editData]);

  const handleClick = () => {
    setOpen(!open);
    setEditData(null);
  };

  const searchByName = (value: string) => {
    if (!value) return setTableData(certsAndLicense);

    const searchTerm = value.trim().toLowerCase();

    const filteredData = certsAndLicense.filter(
      (data: CertificationAndLicense) => {
        const name = data.name?.toLowerCase() ?? '';
        const statePart = name.split(' - ')[1]?.trim() ?? '';
        return (
          statePart.includes(searchTerm) ||
          (!statePart && name.includes(searchTerm))
        );
      }
    );

    setTableData(filteredData);
  };

  const renderCertsAndLicense = () => {
    return (
      <CertsAndLicenseModalList
        tableData={tableData || []}
        setEditData={setEditData}
        searchByName={searchByName}
      />
    );
  };

  const clearData = () => {
    setEditData(null);
    reset(defaultValues);
  };

  const assign = (values: CertsAndLicenseEdit, currentResource: Resource) => {
    const newId = uuidv4();
    const toAssign: SelectedCertificationsAndLicenses = {
      integrationGUID: newId,
      certificationAndLicenseIntegrationGUID: values?.integrationGUID,
      licenseNumber: values.licenseNumber,
      verificationStatus: values?.isActive ? 'active' : 'inactive',
      documentCount: 0,
      issueDate: values?.issueDate,
      expirationDate: values?.expirationDate,
      name: values?.name,
      verificationType: values?.verificationType,
      organization: values?.organization,
    };

    currentResource.selectedCertificationsAndLicenses = [
      ...currentResource.selectedCertificationsAndLicenses,
      toAssign,
    ];

    // update form to match these changes
    setValue(
      'certsAndLicenses',
      updateCertsAndLicenses(currentResource.selectedCertificationsAndLicenses)
    );

    upsertResource(currentResource);
    setEditData(null);
  };

  const edit = (values: CertsAndLicenseEdit, currentResource: Resource) => {
    const currentLicenseIndex =
      resource.selectedCertificationsAndLicenses.flatMap(
        (lic: SelectedCertificationsAndLicenses, index: number) => {
          if (lic.integrationGUID === values?.resourceCertId) {
            return index;
          }
          return [];
        }
      );

    const toAssign: SelectedCertificationsAndLicenses = {
      integrationGUID: values.resourceCertId,
      name: values?.name,
      verificationType: values?.verificationType,
      organization: values?.organization,
      licenseNumber: values?.licenseNumber,
      verificationStatus: values?.isActive ? 'active' : 'inactive',
      documentCount: 0,
      issueDate: values?.issueDate,
      expirationDate: values?.expirationDate,
      certificationAndLicenseIntegrationGUID: values?.integrationGUID,
    };
    currentResource.selectedCertificationsAndLicenses.splice(
      currentLicenseIndex[0],
      1,
      toAssign
    );

    // update form to match these changes
    setValue(
      'certsAndLicenses',
      currentResource.selectedCertificationsAndLicenses.map(
        (certAndLicense) => {
          return {
            integrationGUID: certAndLicense.integrationGUID,
            certificationAndLicenseIntegrationGUID:
              certAndLicense.certificationAndLicenseIntegrationGUID,
            licenseNumber: certAndLicense.licenseNumber,
            verificationStatus: certAndLicense.verificationStatus,
            documentCount: certAndLicense.documentCount,
            issueDate: certAndLicense.issueDate,
            expirationDate: certAndLicense.expirationDate || '',
          };
        }
      )
    );
    upsertResource(currentResource);
    setEditData(null);
  };

  const submitForm = (values: CertsAndLicenseEdit) => {
    const currentResource: Resource = cloneDeep(resource);
    const resourceLicenseNumbers: string[] =
      resource?.selectedCertificationsAndLicenses?.flatMap((certAndLic) =>
        certAndLic.licenseNumber ? certAndLic.licenseNumber : []
      );
    if (values.mode === 'assign') {
      if (
        values?.licenseNumber &&
        resourceLicenseNumbers.includes(values?.licenseNumber)
      ) {
        notifyError('Error', 'License with this license number already exists');

        return;
      } else {
        assign(values, currentResource);
      }
    } else {
      if (!values?.resourceCertId) return;

      edit(values, currentResource);
    }
  };

  React.useEffect(() => {
    if (upsertResourceStatus === 'success') {
      clearData();
      setOpen(false);
    }
  }, [upsertResourceStatus]);

  const onSubmit = () => {
    handleSubmit(submitForm)();
  };

  const renderEditScreen = (editData: CertsAndLicenseEdit | null) => {
    return (
      <CertsAndLicenseModalEdit
        upsertResourceStatus={upsertResourceStatus}
        editData={editData}
        formErrors={formErrors}
        formControl={formControl}
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
        clearData={clearData}
      />
    );
  };

  return (
    <div data-testid="assign-cert-container">
      <Button
        disabled={isReadOnly}
        variant="contained"
        data-testid="assign-cert-button"
      >
        <div
          onClick={handleClick}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <AddIcon sx={{ fontSize: '1.25rem' }} /> ASSIGN CERT OR LICENSE
        </div>
      </Button>
      <Modal
        testId="cert-modal"
        open={open}
        handleClickAway={handleClick}
        title="Certification or License"
        meta={() => {
          return (
            editData &&
            editData.mode === 'assign' && (
              <div>
                <Chip
                  data-testid="drawer-active"
                  label={editData?.isActive ? 'Active' : 'Inactive'}
                  sx={{
                    minWidth: '5em',
                    fontSize: '.85em',
                    color: '#FFFFFF',
                    backgroundColor: editData?.isActive ? '#4CAF50' : '#F44336',
                  }}
                />
              </div>
            )
          );
        }}
      >
        {isEmpty(editData)
          ? renderCertsAndLicense()
          : renderEditScreen(editData)}
      </Modal>
    </div>
  );
};
